import React, { useState, useRef, useEffect } from 'react';
import { FaSpinner } from 'react-icons/fa';

const OtpModal = ({
  isOpen,
  onClose,
  onVerify,
  isLoading,
  title = "Verify OTP",
  description,
  mobile,
  otpLength = 6
}) => {
  const [otp, setOtp] = useState(new Array(otpLength).fill(''));
  const inputRefs = useRef([]);

  // Reset OTP when modal opens
  useEffect(() => {
    if (isOpen) {
      setOtp(new Array(otpLength).fill(''));
      // Auto-focus first input when modal opens
      setTimeout(() => {
        inputRefs.current[0]?.focus();
      }, 100);
    }
  }, [isOpen, otpLength]);

  const handleChange = (e, index) => {
    const value = e.target.value;
    if (/^\d?$/.test(value)) {
      const newOtp = [...otp];
      newOtp[index] = value;
      setOtp(newOtp);

      // Move to next input if value entered and not last
      if (value && index < otpLength - 1) {
        inputRefs.current[index + 1]?.focus();
      }
    }
  };

  const handleKeyDown = (e, index) => {
    if (e.key === 'Backspace') {
      if (otp[index] === '') {
        // Move to previous input if current is empty
        if (index > 0) {
          inputRefs.current[index - 1]?.focus();
        }
      } else {
        // Clear current input
        const newOtp = [...otp];
        newOtp[index] = '';
        setOtp(newOtp);
      }
    } else if (e.key === 'ArrowLeft' && index > 0) {
      inputRefs.current[index - 1]?.focus();
    } else if (e.key === 'ArrowRight' && index < otpLength - 1) {
      inputRefs.current[index + 1]?.focus();
    } else if (e.key === 'Enter' && otp.join('').length === otpLength) {
      // Submit on Enter if OTP is complete
      onVerify(otp.join(''));
    }
  };

  const handlePaste = (e) => {
    e.preventDefault();
    const pasted = e.clipboardData.getData("text").slice(0, otpLength);
    if (!/^\d+$/.test(pasted)) return;

    const newOtp = [...otp];
    pasted.split('').forEach((char, idx) => {
      if (idx < otpLength) {
        newOtp[idx] = char;
      }
    });
    setOtp(newOtp);

    // Focus last filled input
    const nextIndex = Math.min(pasted.length, otpLength - 1);
    inputRefs.current[nextIndex]?.focus();
  };

  const handleVerify = () => {
    const joinedOtp = otp.join('');
    if (joinedOtp.length === otpLength) {
      onVerify(joinedOtp);
    }
  };

  if (!isOpen) return null;

  const joinedOtp = otp.join('');

  return (
    <div className="justify-center items-center flex overflow-x-hidden overflow-y-auto fixed inset-0 z-50 bg-gray-900 bg-opacity-50">
      <div className="relative my-2 mx-auto w-full max-w-md px-4">
        <div className="rounded-xl shadow-lg relative flex flex-col w-full bg-white outline-none focus:outline-none">
          
          {/* Header */}
          <div className="flex justify-between items-center p-4 border-b border-gray-200 rounded-t-xl">
            <div>
              <h3 className="text-xl font-semibold text-gray-800">{title}</h3>
              {description && (
                <p className="text-sm text-gray-600 mt-1">{description}</p>
              )}
            </div>
            <button 
              onClick={onClose}
              className="text-gray-500 hover:text-gray-700 ml-2"
              disabled={isLoading}
              type="button"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </div>

          {/* OTP Boxes */}
          <div className="py-6 px-4">
            <div className="flex justify-center gap-2 md:gap-3" onPaste={handlePaste}>
              {otp.map((digit, index) => (
                <input
                  key={index}
                  type="text"
                  maxLength={1}
                  inputMode="numeric"
                  pattern="\d*"
                  ref={(el) => (inputRefs.current[index] = el)}
                  value={digit}
                  onChange={(e) => handleChange(e, index)}
                  onKeyDown={(e) => handleKeyDown(e, index)}
                  className="w-10 h-12 md:w-12 md:h-14 text-center text-lg font-semibold border-2 border-blue-400 rounded-lg focus:border-blue-600 focus:ring-2 focus:ring-blue-300 transition-all"
                  disabled={isLoading}
                  autoComplete="off"
                />
              ))}
            </div>
          </div>

          {/* Action Buttons */}
          <div className="flex justify-end gap-3 px-4 py-4 border-t border-gray-200">
            <button
              onClick={onClose}
              className="px-6 py-2 text-gray-700 border border-gray-300 rounded-lg hover:bg-gray-100 transition-colors"
              disabled={isLoading}
              type="button"
            >
              Cancel
            </button>
            <button
              onClick={handleVerify}
              disabled={isLoading || joinedOtp.length !== otpLength}
              className={`px-6 py-2 text-white rounded-lg transition-colors ${
                isLoading || joinedOtp.length !== otpLength
                  ? 'bg-blue-400 cursor-not-allowed'
                  : 'bg-blue-600 hover:bg-blue-700'
              }`}
              type="button"
            >
              {isLoading ? (
                <span className="flex items-center justify-center">
                  <FaSpinner className="animate-spin -ml-1 mr-2 h-4 w-4" />
                  Verifying...
                </span>
              ) : (
                'Verify'
              )}
            </button>
          </div>

        </div>
      </div>
    </div>
  );
};

export default OtpModal;
